import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Drawer from "@material-ui/core/Drawer";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Divider from "@material-ui/core/Divider";

import VisibilityIcon from "@material-ui/icons/Visibility";
import BlockIcon from "@material-ui/icons/Block";
import CloseIcon from "@material-ui/icons/Close";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import adminFetch from "../../universal/adminFetch";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const STATUS_COLORS = {
  new: "#2196f3",
  composing: "#ff9800",
  ready: "#4caf50",
  applied: "#8bc34a",
};

const STATUSES = ["all", "new", "composing", "ready", "applied"];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(2),
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  tabs: {
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "& .MuiTab-root": {
      color: "rgba(255,255,255,0.5)",
      textTransform: "none",
      fontWeight: 600,
    },
    "& .Mui-selected": {
      color: "#FF7500",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#FF7500",
    },
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  statusChip: {
    cursor: "pointer",
    fontWeight: 600,
    textTransform: "capitalize",
    fontSize: "0.75rem",
  },
  tableContainer: {
    backgroundColor: "#262829",
    borderRadius: 12,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      color: "rgba(255,255,255,0.7)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.03)",
    },
    "& td": {
      color: "rgba(255,255,255,0.85)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
  },
  drawerPaper: {
    width: 540,
    backgroundColor: "#1C1E20",
    padding: theme.spacing(3),
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  conversationChain: {
    maxHeight: 280,
    overflowY: "auto",
    marginBottom: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
  },
  messageOurs: {
    backgroundColor: "rgba(255,117,0,0.15)",
    marginLeft: theme.spacing(4),
  },
  messageTheirs: {
    backgroundColor: "rgba(255,255,255,0.06)",
    marginRight: theme.spacing(4),
  },
  messageAuthor: {
    fontSize: "0.73rem",
    fontWeight: 700,
    color: "rgba(255,255,255,0.45)",
    marginBottom: theme.spacing(0.5),
  },
  messageText: {
    fontSize: "0.88rem",
    color: "rgba(255,255,255,0.85)",
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
  },
  messageTimestamp: {
    fontSize: "0.68rem",
    color: "rgba(255,255,255,0.3)",
    marginTop: theme.spacing(0.5),
    textAlign: "right",
  },
  drawerActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  rulesForm: {
    maxWidth: 700,
  },
  formField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(255,255,255,0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255,255,255,0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FF7500",
      },
    },
  },
  saveButton: {
    backgroundColor: "#FF7500",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#e06600",
    },
  },
}));

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box pt={2}>{children}</Box>}
    </div>
  );
}

function defaultModelForPlatform(platform) {
  if (platform === "twitter" || platform === "reddit") {
    return { provider: "anthropic", model: "claude-haiku-5-5" };
  }
  return { provider: "openai", model: "gpt-4o-mini" };
}

const DEFAULT_RULES = {
  name: "",
  platform: "reddit",
  systemPrompt: "",
  knowledgeContext: "",
  writingStyle: "",
  constraints: "",
  ...defaultModelForPlatform("reddit"),
  isActive: true,
};

function isDirectMessageOutreach(thread) {
  return thread?.outreachMode === "direct_message";
}

function getOutreachAudienceLabel(audience) {
  return audience === "commenter" ? "Commenter" : "Original poster";
}

export default function SocialEngagementPage({ user }) {
  const classes = useStyles();

  const [activeTab, setActiveTab] = useState(0);

  // Threads state
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerThread, setDrawerThread] = useState(null);
  const [drawerDetail, setDrawerDetail] = useState(null);
  const [drawerDetailLoading, setDrawerDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [outreachAudience, setOutreachAudience] = useState("op");
  const [composing, setComposing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Agent Rules state
  const [agentRules, setAgentRules] = useState(DEFAULT_RULES);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);

  const pollRef = useRef(null);

  // ─── Threads ──────────────────────────────────────────────────────────────

  const fetchThreads = useCallback(async () => {
    if (!user?.userId) return;
    setThreadsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("platform", "reddit");
      params.set("limit", 50);
      params.set("offset", 0);
      const url = `${basePath}/api/v7/admin/social/threads/adpass/${user.userId}?${params}`;
      const resp = await adminFetch(url);
      const data = await resp.json();
      if (data.ok) {
        setThreads(data.threads || []);
      }
    } catch (err) {
      console.error("Failed to fetch social threads", err);
    } finally {
      setThreadsLoading(false);
    }
  }, [user, statusFilter]);

  useEffect(() => {
    if (activeTab !== 0) return;
    fetchThreads();
  }, [fetchThreads, activeTab]);

  // Poll every 10s when there are composing (in-progress) threads
  const hasPendingItems = threads.some((t) => t.status === "composing");

  useEffect(() => {
    if (activeTab !== 0 || !hasPendingItems) return;

    pollRef.current = setInterval(() => {
      fetchThreads();
    }, 10000);

    return () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPendingItems, activeTab, fetchThreads]);

  // ─── Thread detail & drawer ────────────────────────────────────────────────

  const fetchThreadDetail = useCallback(
    async (threadId) => {
      if (!user?.userId) return;
      setDrawerDetailLoading(true);
      try {
        const url = `${basePath}/api/v7/admin/social/threads/${threadId}/adpass/${user.userId}`;
        const resp = await adminFetch(url);
        const data = await resp.json();
        if (data.ok) {
          const thread = data.thread || data;
          setDrawerDetail(thread);
          setReplyText(thread.composedReply || "");
          setOutreachAudience(thread.composedReplyAudience || "op");
        }
      } catch (err) {
        console.error("Failed to fetch thread detail", err);
      } finally {
        setDrawerDetailLoading(false);
      }
    },
    [user]
  );

  useEffect(() => {
    if (drawerThread) {
      fetchThreadDetail(drawerThread._id);
    }
  }, [drawerThread, fetchThreadDetail]);

  const openDrawer = (thread) => {
    setDrawerThread(thread);
    setDrawerDetail(null);
    setReplyText(thread.composedReply || "");
    setOutreachAudience(thread.composedReplyAudience || "op");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerThread(null);
    setDrawerDetail(null);
    setReplyText("");
    setOutreachAudience("op");
  };

  const handleCompose = async () => {
    if (!drawerThread || !user?.userId) return;
    const detailThread = drawerDetail || drawerThread;
    const isDmOutreach = isDirectMessageOutreach(detailThread);
    setComposing(true);
    try {
      const url = `${basePath}/api/v7/admin/social/threads/${drawerThread._id}/compose/adpass/${user.userId}`;
      const resp = await adminFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isDmOutreach ? { outreachAudience } : {}),
      });
      const data = await resp.json();
      if (data.ok) {
        setReplyText(data.composedReply || "");
        fetchThreadDetail(drawerThread._id);
        fetchThreads();
      }
    } catch (err) {
      console.error("Failed to compose reply", err);
    } finally {
      setComposing(false);
    }
  };

  const handleApply = async () => {
    if (!drawerThread || !user?.userId || !replyText.trim()) return;
    setApplying(true);
    try {
      const url = `${basePath}/api/v7/admin/social/threads/${drawerThread._id}/apply/adpass/${user.userId}`;
      const resp = await adminFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ replyText }),
      });
      const data = await resp.json();
      if (data.ok) {
        fetchThreads();
        closeDrawer();
      }
    } catch (err) {
      console.error("Failed to apply reply", err);
    } finally {
      setApplying(false);
    }
  };

  const handleDismiss = async (threadId) => {
    if (!user?.userId) return;
    setDismissing(true);
    try {
      const url = `${basePath}/api/v7/admin/social/threads/${threadId}/dismiss/adpass/${user.userId}`;
      const resp = await adminFetch(url, { method: "POST" });
      const data = await resp.json();
      if (data.ok) {
        fetchThreads();
        if (drawerOpen && drawerThread?._id === threadId) {
          closeDrawer();
        }
      }
    } catch (err) {
      console.error("Failed to dismiss thread", err);
    } finally {
      setDismissing(false);
    }
  };

  const handleTrainingFeedback = async (label) => {
    if (!drawerThread || !user?.userId) return;
    setSavingFeedback(true);
    try {
      const url = `${basePath}/api/v7/admin/social/threads/${drawerThread._id}/training-feedback/adpass/${user.userId}`;
      const resp = await adminFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label,
          reason: label === "good_fit"
            ? "Marked as a good fit from admin review."
            : "Marked as a bad fit from admin review.",
        }),
      });
      const data = await resp.json();
      if (data.ok) {
        fetchThreadDetail(drawerThread._id);
        fetchThreads();
      }
    } catch (err) {
      console.error("Failed to save training feedback", err);
    } finally {
      setSavingFeedback(false);
    }
  };

  // ─── Agent Rules ───────────────────────────────────────────────────────────

  const fetchAgentRules = useCallback(async () => {
    if (!user?.userId) return;
    setRulesLoading(true);
    try {
      const url = `${basePath}/api/v7/admin/social/agent-rules/adpass/${user.userId}`;
      const resp = await adminFetch(url);
      const data = await resp.json();
      if (data.ok && Array.isArray(data.rules) && data.rules.length > 0) {
        const rule = data.rules[0];
        const platform = rule.platform || "reddit";
        const modelDefaults = defaultModelForPlatform(platform);
        setAgentRules({
          _id: rule._id,
          name: rule.name || "",
          platform,
          systemPrompt: rule.systemPrompt || "",
          knowledgeContext: rule.knowledgeContext || "",
          writingStyle: rule.writingStyle || "",
          constraints: Array.isArray(rule.constraints)
            ? rule.constraints.join("\n")
            : rule.constraints || "",
          model: rule.model || modelDefaults.model,
          provider: rule.provider || modelDefaults.provider,
          isActive: rule.isActive !== false,
        });
      }
    } catch (err) {
      console.error("Failed to fetch agent rules", err);
    } finally {
      setRulesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchAgentRules();
    }
  }, [activeTab, fetchAgentRules]);

  const saveAgentRules = async () => {
    if (!user?.userId) return;
    setRulesSaving(true);
    try {
      const url = `${basePath}/api/v7/admin/social/agent-rules/adpass/${user.userId}`;
      const body = {
        ...agentRules,
        constraints: agentRules.constraints
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const resp = await adminFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await resp.json();
    } catch (err) {
      console.error("Failed to save agent rules", err);
    } finally {
      setRulesSaving(false);
    }
  };

  const updateRule = (field) => (e) => {
    const value = field === "isActive" ? e.target.checked : e.target.value;
    setAgentRules((prev) => ({ ...prev, [field]: value }));
  };

  // The drawer shows full detail when loaded, falls back to row data while loading
  const detail = drawerDetail || drawerThread;
  const isDmOutreach = isDirectMessageOutreach(detail);
  const outreachAudiences = Array.isArray(detail?.outreachAudiences) && detail.outreachAudiences.length > 0
    ? detail.outreachAudiences
    : ["op"];

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Social Engagement</Typography>

      <div className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
        >
          <Tab label="Threads" />
          <Tab label="Agent Rules" />
        </Tabs>
      </div>

      {/* ── Tab 0: Threads ── */}
      <TabPanel value={activeTab} index={0}>
        <Box className={classes.filterRow}>
          <Typography
            variant="body2"
            style={{ color: "rgba(255,255,255,0.45)", marginRight: 4 }}
          >
            Status:
          </Typography>
          {STATUSES.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              className={classes.statusChip}
              onClick={() => setStatusFilter(s)}
              style={{
                backgroundColor:
                  s === statusFilter
                    ? STATUS_COLORS[s] || "#FF7500"
                    : "rgba(255,255,255,0.1)",
                color: "#fff",
              }}
            />
          ))}
        </Box>

        {threadsLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress style={{ color: "#FF7500" }} />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Subreddit</TableCell>
                  <TableCell align="center">Edge AI</TableCell>
                  <TableCell align="center">Local</TableCell>
                  <TableCell align="center">Upvotes</TableCell>
                  <TableCell align="center">Comments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {threads.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      style={{ color: "rgba(255,255,255,0.4)", padding: 40 }}
                    >
                      No threads found.
                    </TableCell>
                  </TableRow>
                ) : (
                  threads.map((thread) => (
                    <TableRow
                      key={thread._id}
                      className={classes.row}
                      onClick={() => openDrawer(thread)}
                    >
                      <TableCell style={{ textTransform: "capitalize" }}>
                        {thread.platform || "reddit"}
                      </TableCell>
                      <TableCell
                        style={{
                          maxWidth: 240,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {thread.permalink ? (
                          <a
                            href={thread.permalink}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "#FF7500", textDecoration: "none" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {thread.title}
                          </a>
                        ) : (
                          thread.title
                        )}
                      </TableCell>
                      <TableCell>{thread.author || "—"}</TableCell>
                      <TableCell>
                        {thread.subreddit ? `r/${thread.subreddit}` : "—"}
                      </TableCell>
                      <TableCell align="center">
                        {thread.aiScore != null ? thread.aiScore : "—"}
                        {thread.aiRelevant === true && (
                          <Typography variant="caption" style={{ display: "block", color: "#4caf50" }}>
                            {thread.humanVerified ? "human verified" : "relevant"}
                          </Typography>
                        )}
                        {thread.aiRelevant === false && (
                          <Typography variant="caption" style={{ display: "block", color: "rgba(255,255,255,0.45)" }}>
                            not relevant
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {thread.localScore ?? "—"}
                      </TableCell>
                      <TableCell align="center">
                        {thread.upvotes ?? "—"}
                      </TableCell>
                      <TableCell align="center">
                        {thread.numComments ?? thread.commentCount ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={thread.pendingApplyAt ? "queued" : thread.status}
                          size="small"
                          style={{
                            backgroundColor:
                              thread.pendingApplyAt ? "#9c27b0" : STATUS_COLORS[thread.status] || "#757575",
                            color: "#fff",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                        {thread.lastApplyError && (
                          <Typography variant="caption" style={{ display: "block", color: "#f44336", marginTop: 4 }}>
                            {thread.lastApplyError}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(thread.lastSyncedAt)}</TableCell>
                      <TableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="View detail">
                          <IconButton
                            size="small"
                            onClick={() => openDrawer(thread)}
                          >
                            <VisibilityIcon
                              fontSize="small"
                              style={{ color: "rgba(255,255,255,0.6)" }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dismiss">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDismiss(thread._id)}
                              disabled={
                                thread.status === "dismissed" || dismissing
                              }
                            >
                              <BlockIcon
                                fontSize="small"
                                style={{
                                  color:
                                    thread.status === "dismissed"
                                      ? "rgba(255,255,255,0.2)"
                                      : "#f44336",
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ── Tab 1: Agent Rules ── */}
      <TabPanel value={activeTab} index={1}>
        {rulesLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress style={{ color: "#FF7500" }} />
          </Box>
        ) : (
          <div className={classes.rulesForm}>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              value={agentRules.name}
              onChange={updateRule("name")}
              fullWidth
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.formField}
            >
              <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>
                Platform
              </InputLabel>
              <Select
                value={agentRules.platform}
                onChange={(event) => {
                  const platform = event.target.value;
                  const modelDefaults = defaultModelForPlatform(platform);
                  setAgentRules((prev) => ({
                    ...prev,
                    platform,
                    provider: modelDefaults.provider,
                    model: modelDefaults.model,
                  }));
                }}
                label="Platform"
                style={{ color: "#fff" }}
              >
                <MenuItem value="reddit">Reddit</MenuItem>
                <MenuItem value="twitter">Twitter</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="System Prompt"
              variant="outlined"
              size="small"
              value={agentRules.systemPrompt}
              onChange={updateRule("systemPrompt")}
              fullWidth
              multiline
              rows={6}
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <TextField
              label="Knowledge Context"
              variant="outlined"
              size="small"
              value={agentRules.knowledgeContext}
              onChange={updateRule("knowledgeContext")}
              fullWidth
              multiline
              rows={4}
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <TextField
              label="Writing Style"
              variant="outlined"
              size="small"
              value={agentRules.writingStyle}
              onChange={updateRule("writingStyle")}
              fullWidth
              multiline
              rows={3}
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <TextField
              label="Constraints (one per line)"
              variant="outlined"
              size="small"
              value={agentRules.constraints}
              onChange={updateRule("constraints")}
              fullWidth
              multiline
              rows={4}
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <TextField
              label="Model"
              variant="outlined"
              size="small"
              value={agentRules.model}
              onChange={updateRule("model")}
              fullWidth
              className={classes.formField}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
            />

            <FormControl
              variant="outlined"
              size="small"
              fullWidth
              className={classes.formField}
            >
              <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>
                Provider
              </InputLabel>
              <Select
                value={agentRules.provider}
                onChange={updateRule("provider")}
                label="Provider"
                style={{ color: "#fff" }}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="anthropic">Anthropic</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={agentRules.isActive}
                  onChange={updateRule("isActive")}
                  color="primary"
                />
              }
              label={
                <Typography style={{ color: "rgba(255,255,255,0.85)" }}>
                  Active
                </Typography>
              }
              style={{ marginBottom: 16, display: "block" }}
            />

            <Box>
              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={saveAgentRules}
                disabled={rulesSaving}
              >
                {rulesSaving ? "Saving…" : "Save Rules"}
              </Button>
            </Box>
          </div>
        )}
      </TabPanel>

      {/* ── Side Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        classes={{ paper: classes.drawerPaper }}
      >
        {drawerThread && (
          <>
            {/* Header */}
            <div className={classes.drawerHeader}>
              <Box flex={1} pr={2}>
                <Typography
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1rem",
                    lineHeight: 1.4,
                  }}
                >
                  {detail?.title || drawerThread.title}
                </Typography>
                {(detail?.subreddit || drawerThread.subreddit) && (
                  <Typography
                    variant="body2"
                    style={{ color: "rgba(255,255,255,0.45)", marginTop: 4 }}
                  >
                    r/{detail?.subreddit || drawerThread.subreddit}
                    {(detail?.author || drawerThread.author) &&
                      ` · ${detail?.author || drawerThread.author}`}
                    {(detail?.flair || drawerThread.flair) &&
                      ` · ${detail?.flair || drawerThread.flair}`}
                  </Typography>
                )}
                {(detail?.permalink || drawerThread.permalink) && (
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    <a
                      href={detail?.permalink || drawerThread.permalink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#FF7500", textDecoration: "none" }}
                    >
                      Open Reddit thread
                    </a>
                  </Typography>
                )}
                {(detail?.humanVerified || drawerThread.humanVerified) && (
                  <Typography
                    variant="body2"
                    style={{ color: "#4caf50", marginTop: 6, fontSize: "0.8rem" }}
                  >
                    Human-verified import
                  </Typography>
                )}
                {(detail?.trainingLabel || drawerThread.trainingLabel) && (
                  <Typography
                    variant="body2"
                    style={{ color: "rgba(255,255,255,0.55)", marginTop: 4, fontSize: "0.78rem" }}
                  >
                    Training: {detail?.trainingLabel || drawerThread.trainingLabel}
                  </Typography>
                )}
                {isDmOutreach && (
                  <Typography
                    variant="body2"
                    style={{ color: "#FF7500", marginTop: 6, fontSize: "0.8rem" }}
                  >
                    FACEIT DM outreach - compose copy to paste manually.
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Chip
                  label={detail?.status || drawerThread.status}
                  size="small"
                  style={{
                    backgroundColor:
                      STATUS_COLORS[detail?.status || drawerThread.status] ||
                      "#757575",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                />
                {(detail?.pendingApplyAt || detail?.lastApplyError) && (
                  <Typography
                    variant="caption"
                    style={{ color: detail?.lastApplyError ? "#f44336" : "rgba(255,255,255,0.55)" }}
                  >
                    {detail?.lastApplyError || "Reply queued for browser extension."}
                  </Typography>
                )}
                <IconButton size="small" onClick={closeDrawer}>
                  <CloseIcon style={{ color: "rgba(255,255,255,0.6)" }} />
                </IconButton>
              </Box>
            </div>

            {detail?.excerpt && (
              <Box mb={2}>
                <Typography
                  variant="body2"
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.85rem",
                    lineHeight: 1.6,
                  }}
                >
                  {detail.excerpt}
                </Typography>
              </Box>
            )}

            {(detail?.postType || detail?.mediaUrl || detail?.thumbnailUrl || detail?.outboundUrl) && (
              <Box mb={2}>
                <Typography variant="body2" style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>
                  Media: {detail?.postType || "unknown"}
                </Typography>
                {(detail?.mediaUrl || detail?.thumbnailUrl || detail?.outboundUrl) && (
                  <Typography variant="body2" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                    <a
                      href={detail.mediaUrl || detail.thumbnailUrl || detail.outboundUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#FF7500", textDecoration: "none" }}
                    >
                      Open media/source
                    </a>
                  </Typography>
                )}
              </Box>
            )}

            {detail?.aiReason && (
              <Box mb={2}>
                <Typography variant="body2" style={{ color: "#FF7500", fontSize: "0.8rem", lineHeight: 1.5 }}>
                  Edge AI: {detail.aiRelevant ? "Relevant" : "Not relevant"} · Score {detail.aiScore ?? "—"} · {detail.aiReason}
                </Typography>
              </Box>
            )}

            <Divider
              style={{
                backgroundColor: "rgba(255,255,255,0.1)",
                marginBottom: 16,
              }}
            />

            {drawerDetailLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} style={{ color: "#FF7500" }} />
              </Box>
            ) : (
              <>
                {/* Conversation chain */}
                {detail?.conversationChain &&
                  detail.conversationChain.length > 0 && (
                    <Box mb={2}>
                      <Typography
                        style={{
                          color: "rgba(255,255,255,0.4)",
                          fontWeight: 700,
                          marginBottom: 8,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Conversation
                      </Typography>
                      <div className={classes.conversationChain}>
                        {detail.conversationChain.map((msg, i) => (
                          <div
                            key={i}
                            className={`${classes.message} ${
                              msg.isOurs
                                ? classes.messageOurs
                                : classes.messageTheirs
                            }`}
                          >
                            <Typography className={classes.messageAuthor}>
                              {msg.isOurs ? "Us" : msg.author}
                            </Typography>
                            <Typography className={classes.messageText}>
                              {msg.text}
                            </Typography>
                            {msg.timestamp && (
                              <Typography className={classes.messageTimestamp}>
                                {formatDate(msg.timestamp)}
                              </Typography>
                            )}
                          </div>
                        ))}
                      </div>
                      <Divider
                        style={{
                          backgroundColor: "rgba(255,255,255,0.1)",
                          marginBottom: 16,
                        }}
                      />
                    </Box>
                  )}

                {isDmOutreach && (
                  <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.formField}
                  >
                    <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>
                      DM audience
                    </InputLabel>
                    <Select
                      value={outreachAudience}
                      onChange={(e) => setOutreachAudience(e.target.value)}
                      label="DM audience"
                      style={{ color: "#fff" }}
                    >
                      {outreachAudiences.map((audience) => (
                        <MenuItem key={audience} value={audience}>
                          {getOutreachAudienceLabel(audience)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Reply textarea */}
                <TextField
                  label={isDmOutreach ? "Composed DM" : "Composed Reply"}
                  variant="outlined"
                  size="small"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  fullWidth
                  multiline
                  rows={7}
                  style={{ marginBottom: 16 }}
                  InputProps={{ style: { color: "#fff", fontSize: "0.9rem" } }}
                  InputLabelProps={{
                    style: { color: "rgba(255,255,255,0.5)" },
                  }}
                />

                {/* Action buttons */}
                <div className={classes.drawerActions}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCompose}
                    disabled={composing}
                    startIcon={
                      composing ? (
                        <CircularProgress
                          size={14}
                          style={{ color: "#FF7500" }}
                        />
                      ) : (
                        <AutorenewIcon />
                      )
                    }
                    style={{ borderColor: "#FF7500", color: "#FF7500" }}
                  >
                    {composing ? "Composing…" : isDmOutreach ? "Compose DM with AI" : "Compose with AI"}
                  </Button>

                  {isDmOutreach ? (
                    <Typography
                      variant="body2"
                      style={{ color: "rgba(255,255,255,0.55)", alignSelf: "center" }}
                    >
                      Copy this DM text and send it manually in Reddit.
                    </Typography>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApply}
                      disabled={applying || !replyText.trim()}
                      startIcon={<CheckCircleOutlineIcon />}
                      style={{ backgroundColor: "#4caf50", color: "#fff" }}
                    >
                      {applying ? "Applying…" : "Apply Reply"}
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDismiss(drawerThread._id)}
                    disabled={
                      dismissing ||
                      (detail?.status || drawerThread.status) === "dismissed"
                    }
                    startIcon={<BlockIcon />}
                    style={{ borderColor: "#f44336", color: "#f44336" }}
                  >
                    Dismiss
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleTrainingFeedback("good_fit")}
                    disabled={savingFeedback}
                    style={{ borderColor: "#4caf50", color: "#4caf50" }}
                  >
                    Mark Good Fit
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleTrainingFeedback("bad_fit")}
                    disabled={savingFeedback}
                    style={{ borderColor: "#f44336", color: "#f44336" }}
                  >
                    Mark Bad Fit
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

SocialEngagementPage.propTypes = {
  user: PropTypes.object,
};
